<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.stable_request_data") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #F7941E">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <!-- Start:: Basic Information -->
        <div class="row">
          <div class="col-12 mb-3">
            <h5 class="section-title">{{ $t("PLACEHOLDERS.basic_info") }}</h5>
          </div>

          <!-- Start:: Stable Image (Avatar) -->
          <base-image-upload-input
            v-if="data.avatar"
            col="12"
            identifier="avatar"
            :placeholder="$t('PLACEHOLDERS.stable_image')"
            :preSelectedImage="data.avatar"
            disabled
          />
          <!-- End:: Stable Image -->

          <!-- Start:: Stable Name -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.stable_name')"
            v-model.trim="data.name"
            disabled
          />
          <!-- End:: Stable Name -->

          <!-- Start:: Full Mobile -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model.trim="data.full_mobile"
            disabled
          />
          <!-- End:: Full Mobile -->

          <!-- Start:: Email -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.email')"
            v-model.trim="data.email"
            disabled
          />
          <!-- End:: Email -->

          <!-- Start:: City -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.city')"
            v-model.trim="data.city"
            disabled
          />
          <!-- End:: City -->

          <!-- Start:: Commercial Registration (if exists) -->
          <base-input
            v-if="data.commercial_registration_number"
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.commercial_registration_number')"
            v-model.trim="data.commercial_registration_number"
            disabled
          />
          <!-- End:: Commercial Registration -->


          <!-- Start:: Map Section -->
          <div v-if="data.latitude && data.longitude" class="col-12 mt-4">
            <h5 class="section-title">{{ $t("PLACEHOLDERS.location") }}</h5>
            <div class="map_wrapper">
              <GmapMap
                :center="{
                  lat: parseFloat(data.latitude),
                  lng: parseFloat(data.longitude),
                }"
                :zoom="13"
                map-type-id="terrain"
                style="width: 100%; height: 500px"
              >
                <GmapMarker
                  :position="{
                    lat: parseFloat(data.latitude),
                    lng: parseFloat(data.longitude),
                  }"
                  :clickable="false"
                  :draggable="false"
                />
              </GmapMap>
            </div>
          </div>
          <!-- End:: Map Section -->

        </div>
        <!-- End:: Basic Information -->
      </form>
    </div>
    <!-- END:: Single Step Form Content -->

    <!-- Start:: Image Modal -->
    <v-dialog v-model="imageModal" max-width="800px">
      <v-card v-if="selectedImage">
        <v-card-title>{{ $t("PLACEHOLDERS.stable_image") }}</v-card-title>
        <v-card-text>
          <img :src="selectedImage" alt="" style="width: 100%; height: auto;" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="imageModal = false">{{ $t("BUTTONS.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Image Modal -->

    <!-- Start:: Reject Modal -->
    <v-dialog v-model="rejectModal" max-width="500px">
      <v-card>
        <v-card-title>{{ $t("TITLES.reject_request") }}</v-card-title>
        <v-card-text>
          <base-input
            col="12"
            rows="4"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.reason_reject')"
            v-model.trim="rejectReason"
            required
          />
        </v-card-text>
        <v-card-actions>
          <v-btn 
            color="error" 
            @click="rejectRequest"
            :disabled="!rejectReason || rejectReason.length < 3 || isProcessing"
          >
            {{ $t("BUTTONS.reject") }}
          </v-btn>
          <v-btn @click="rejectModal = false">{{ $t("BUTTONS.cancel") }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Reject Modal -->
  </div>
</template>

<script>
export default {
  name: "StableRequestShow",

  computed: {
    hasStableImages() {
      return this.stableImages.length > 0;
    },

    stableImages() {
      const images = [];
      
      // Add stable_data images if they exist
      if (this.data.stable_data && this.data.stable_data.images && this.data.stable_data.images.length > 0) {
        this.data.stable_data.images.forEach(image => {
          images.push(image.url || image);
        });
      }
      
      return images;
    },
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      isProcessing: false,
      // End:: Loader Control Data

      // Start:: Data Collection
      data: {
        id: null,
        request_number: null,
        name: null,
        country_code: null,
        mobile: null,
        full_mobile: null,
        email: null,
        city: null,
        city_id: null,
        location: null,
        latitude: null,
        longitude: null,
        avatar: null,
        commercial_registration_number: null,
        stable_data: null,
        is_approved: null,
        is_active: null,
        request_date: null,
        created_at: null,
        updated_at: null,
      },
      // End:: Data Collection

      // Modals
      imageModal: false,
      selectedImage: null,
      rejectModal: false,
      rejectReason: "",
    };
  },

  methods: {
    // Start:: Format Date
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US');
    },
    // End:: Format Date

    // Start:: Get Data To Edit
    async getDataToEdit() {
      this.isWaitingRequest = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `stable-join-requests/${this.$route.params.id}`,
        });

        // Start:: Set Data from response
        const stableRequestData = res.data.data.stable_request;
        
        this.data.id = stableRequestData.id;
        this.data.request_number = stableRequestData.request_number;
        this.data.name = stableRequestData.name;
        this.data.country_code = stableRequestData.country_code;
        this.data.mobile = stableRequestData.mobile;
        this.data.full_mobile = stableRequestData.full_mobile;
        this.data.email = stableRequestData.email;
        this.data.city = stableRequestData.city;
        this.data.city_id = stableRequestData.city_id;
        this.data.location = stableRequestData.location;
        this.data.latitude = stableRequestData.latitude;
        this.data.longitude = stableRequestData.longitude;
        this.data.avatar = stableRequestData.avatar;
        this.data.commercial_registration_number = stableRequestData.commercial_registration_number;
        this.data.stable_data = stableRequestData.stable_data;
        this.data.is_approved = stableRequestData.is_approved;
        this.data.is_active = stableRequestData.is_active;
        this.data.request_date = stableRequestData.request_date;
        this.data.created_at = stableRequestData.created_at;
        this.data.updated_at = stableRequestData.updated_at;
        // End:: Set Data

        this.isWaitingRequest = false;
      } catch (error) {
        this.isWaitingRequest = false;
        console.log(error.response.data.message);
        this.$message.error(error.response.data.message);
        this.$router.push({ path: "/join_stables/all" });
      }
    },
    // End:: Get Data To Edit

    // Start:: Open Image Modal
    openImageModal(imageUrl) {
      this.selectedImage = imageUrl;
      this.imageModal = true;
    },
    // End:: Open Image Modal

    // Start:: Open Reject Modal
    openRejectModal() {
      this.rejectModal = true;
      this.rejectReason = "";
    },
    // End:: Open Reject Modal

    // Start:: Accept Request
    async acceptRequest() {
      this.isProcessing = true;
      try {
        await this.$axios({
          method: "POST",
          url: `stable-join-requests/${this.data.id}/accept`,
        });
        this.$message.success(this.$t("MESSAGES.request_accepted"));
        this.$router.push({ path: "/join_stables/all" });
      } catch (error) {
        this.$message.error(error.response.data.message);
      }
      this.isProcessing = false;
    },
    // End:: Accept Request

    // Start:: Reject Request
    async rejectRequest() {
      this.isProcessing = true;
      try {
        await this.$axios({
          method: "POST",
          url: `stable-join-requests/${this.data.id}/reject`,
          data: {
            reason: this.rejectReason
          }
        });
        this.$message.success(this.$t("MESSAGES.request_rejected"));
        this.rejectModal = false;
        this.$router.push({ path: "/join_stables/all" });
      } catch (error) {
        this.$message.error(error.response.data.message);
      }
      this.isProcessing = false;
    },
    // End:: Reject Request
  },

  async created() {
    // Start:: Fire Methods
    await this.getDataToEdit();
    // End:: Fire Methods
  },
};
</script>
<style scoped>
.preview-container img {
  max-width: 100%;
  max-height: 200px;
  border: 1px solid #ccc;
  border-radius: 15px;
}

.section-title {
  color: #2c3e50;
  border-bottom: 2px solid #3498db;
  padding-bottom: 8px;
  margin-bottom: 20px;
  font-weight: bold;
}

.map_wrapper {
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #eee;
  margin-top: 10px;
}

.images-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 5px;
}

.image-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.image-wrapper:hover {
  transform: scale(1.05);
  cursor: pointer;
}

.stable-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  transition: opacity 0.3s ease;
}

.stable-image:hover {
  opacity: 0.9;
}

.btn {
  padding: 12px 20px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn i {
  margin-right: 8px;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .images-container {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
  
  .stable-image {
    height: 120px;
  }
}
</style>
